<style lang="postcss">
	.close-button {
		opacity: 0.5;
		outline: none;
		position: fixed;
		top: 0;
		right: 0;
		z-index: 100;
		padding: 0.9rem 0.9rem 0.2rem 0.6rem;
	}
	.close-button:hover {
		opacity: 1;
	}
	.progress-container {
		position: fixed;
		top: 1rem;
		right: 1rem;
		z-index: 50;
	}
	.save-progress-indicator {
		display: block;
		position: fixed;
		bottom: 0.5rem;
		left: 1rem;
		z-index: 10;
		font-weight: 460;
	}

	#form-cover {
		container: form-cover / inline-size;
	}

	#form {
		position: relative;
		display: flex;
		flex-direction: column;
		max-width: 100%;
		margin-left: auto;
		margin-right: auto;
	}

	.form-message {
		display: flex;
		flex-direction: column;
		align-items: center;
		justify-content: flex-start;
		gap: 0.5rem;
		margin: auto;
	}

	.form-message-content {
		width: 100%;
		padding: 3rem 4rem;
		text-align: center;
		margin: auto;
	}

	.form-content {
		display: flex;
		flex-direction: column;
		width: 100%;
	}

	.form-disabled-message {
		display: flex;
		flex-direction: column;
		align-items: center;
		justify-content: center;
		gap: 0.5rem;
		padding: 4rem 0;
		margin: auto;
	}
	@media (max-width: 768px) {
		.save-progress-indicator {
			font-size: 12.5px;
			left: auto;
			bottom: auto;
			top: 0.5rem;
			right: 0.75rem;
			opacity: 0.65;
			font-weight: 480;
		}
	}
</style>

<script lang="ts">
	import "$lib/misc/shared.css";
	import { createDraft, finishDraft } from "immer";
	import { PUBLIC_FLY_PRIMARY_REGION } from "$env/static/public";
	import MultiStep from "$form/MultiStep.svelte";
	import Checkmark from "$form/Checkmark.svelte";
	import BackgroundLayers from "$form/BackgroundLayers.svelte";
	import { v4 as uuid } from "@lukeed/uuid";
	import dayjs from "dayjs";
	import { BROWSER } from "esm-env";
	import { onDestroy, onMount, setContext, getContext, tick } from "svelte";
	import type { Writable } from "svelte/store";
	import { writable } from "svelte/store";
	import { pageIn, bezier } from "$lib/transitions";
	import { sineIn } from "svelte/easing";
	import { encode, decode } from "js-base64";

	if (import.meta.hot) {
		import.meta.hot.on("vite:beforeFullReload", () => {
			throw "(skipping full reload)";
		});
	}

	const reducedAnimation = BROWSER
		? window.matchMedia(`(prefers-reduced-motion: reduce)`).matches === true
		: false;

	import {
		clone,
		computeFormState,
		computeFieldMap,
		findField,
		getUTMParameters,
		prepareVisit,
		areConditionsFulfilled,
		hasLookupChanged,
		performLookup,
		updateFormValuesBasedOnLookup,
		makeReadable,
		debounce,
		applyFormValue,
		getValuesFromUrl,
		scrollIntoViewIfNeeded,
		calculateFormProgress,
		loadSVGElement,
		areBackgroundsEqual,
		findFieldToScroll,
		sanitizeReference,
		getFormValuesArray
	} from "$lib/helpers/functions";
	import { t } from "$lib/helpers/translate";
	import type { CreatePaymentMethodData } from "@stripe/stripe-js";
	import equal from "fast-deep-equal";
	import { browser } from "$app/environment";
	import { isValidEmail } from "$src/lib/helpers/validate";

	export let disabled: boolean = false;
	export let disabledMessage: string | null = null;
	export let disabledType: string | null = null;
	export let test: boolean = false;
	export let cache: boolean = false;
	export let record: boolean = false;
	export let formEmbed: FormEmbed;
	export let formLanguage: Language;
	export let plan: PlanShort;
	export let fromCache: boolean = false;
	export let countryCode: string | null = null;
	export let iframe: boolean = false;
	export let popup: boolean = false;
	export let seamless: boolean = false;
	export let translate: { [key in TranslateKeys]: string };
	export let visitorInfo: VisitorInfo;
	export let requestUrl: string = null;
	export let bot: boolean = false;
	export let device: string = "desktop";

	export let metadata: Metadata = {
		inIframe: iframe,
		ipAddress: visitorInfo.ipAddress ?? null,
		formId: formEmbed.key,
		formName: formEmbed.name,
		formLanguage: formLanguage.value,
		pageUrl: "",
		pageTitle: "",
		dateTime: "",
		currentDate: dayjs().format("YYYY-MM-DD"),
		country: visitorInfo.country,
		region: visitorInfo.region,
		city: visitorInfo.city,
		timezone: BROWSER
			? Intl.DateTimeFormat().resolvedOptions().timeZone || null
			: null
	};

	let saveProgressTimeout: NodeJS.Timeout;
	let saveProgressMessage: Writable<string> = writable("");
	let uploadsInProgress: Writable<{ [key: string]: number }> = writable({});
	let redirectWithin = false;
	let windowInnerHeight = BROWSER ? window.innerHeight : 800;
	let newlyVisibleFields: string[] = [];
	let mounted = writable(false);
	let formMessageRef: HTMLDivElement;
	let formRef: HTMLFormElement;
	let formCoverRef: HTMLDivElement;
	let fields: Field[] = formEmbed.fields;
	let fieldDisplay: FieldDisplay[] = formEmbed.fieldDisplay;
	let pageDisplay: PageDisplay[] = formEmbed.pageDisplay;
	let configDisplay: Config = formEmbed.configDisplay;
	let pages: Page[] = formEmbed.pages;
	let styling: Styling = formEmbed.styling;
	let config: Config = formEmbed.config;
	let stripe = writable<StripeInstance | null>(null);
	let formMeta = writable(formEmbed.formMeta);
	let formStripe = writable<FormStripe>(defaultFormStripe());
	let formError: string | null = null;
	let useStripe: boolean = false;
	let formMessage: string | null = null;
	let formLoading: boolean = false;
	let eagerOnNext: boolean = false;
	let lookupState: any = {};
	let extraValues: Writable<ExtraValues> = writable<ExtraValues>({});
	let readOnlyValues: Writable<FormValues> = writable<FormValues>({});
	let lookupsRecent: number = 0;
	let clearLookupsRecent: NodeJS.Timeout | null = null;
	let key: string = formEmbed.key;
	let invalid_fields = writable<FieldDisplay[]>([]);
	let remoteValidation = writable<RemoteValidation[]>(
		getInitialRemoteValidation(formEmbed.fields)
	);
	let past_google_events: string[] = [];
	let hubspotutk: string = null;

	let partialLastRecord: any = null;
	let partial_loading: boolean = false;

	let formValuesDefault = formEmbed.formValues;
	let accessCode = "";
	let nonce: string = null;
	let response_id: string | null = null;
	let submit_partial_controller = new AbortController();
	let submit_partial_signal = submit_partial_controller.signal;

	let timing: Writable<Timing> = writable(
		reducedAnimation || styling.disableAnimations || bot
			? {
					duration: 0,
					out: 0,
					in: 0
				}
			: {
					duration: 500,
					out: 0.15,
					in: 0.65
				}
	);

	t.set(translate);

	loadSVGElement();

	let session_id = uuid();
	let session: VisitSession = {
		session_id,
		created_at: dayjs().toISOString()
	};
	let visit = writable<Visit>({
		session_id,
		created_at: session.created_at,
		form_id: formEmbed.id,
		test,
		lookups_performed: 0,
		start: false,
		last_field: null,
		last_page: null,
		time_spent: null,
		device: device,
		tracking: {},
		fields: {},
		pages: {}
	});

	let currentPageIdDefault = styling.welcomeEnabled ? "welcome" : pages[0].id;
	const trackingParams = getUTMParameters(requestUrl ?? "");
	if (trackingParams.utm_medium === "email") {
		// currentPageIdDefault = pages[0].id;
	}

	if (BROWSER) {
		const url = new URL(window.location.href);
		$visit.tracking = getUTMParameters(window.location.href);
		$visit.tracking.page_url = url.origin + url.pathname;
		$visit.tracking.page_url_raw = url.href;

		const parsed = getFormFromLocalStorage();
		if ($visit.tracking?.utm_medium === "email") {
			// currentPageIdDefault = pages[0].id;
		}
		if (parsed) {
			if (config.saveProgress === true) {
				formValuesDefault = parsed.formValues;
			}
			if (parsed.accessCode) {
				accessCode = parsed.accessCode;
			}
		}
	}

	const urlValues = getValuesFromUrl(requestUrl, fields);
	const newValuesDefault = applyFormValue(formValuesDefault, urlValues, fields);
	if (newValuesDefault) {
		formValuesDefault = newValuesDefault;
	}
	if (iframe === false) {
		$extraValues = prepareUrlReferences(requestUrl, $extraValues);
	}

	let askAccessCode: boolean =
		test === false ? (config.requireAccessCode ?? false) : false;
	let currentPageId: string = currentPageIdDefault;
	let formValues = writable<FormValues>(clone(formValuesDefault));
	let taintedFields: string[] = [];
	let hasVisitChanged: boolean = false;
	let isLastVisit: boolean = false;
	let form: number = 0;

	$: form = calculateFormProgress(fieldDisplay, pageDisplay, currentPageId);

	if (BROWSER) {
		(window as any).changeCurrentPage = (id: string) => {
			currentPageId = id;
		};
	}

	let visitMeta = writable<VisitMeta>({
		trackingFields: {},
		start: null
	});
	let lastVisit: string;

	// Constants
	let runLookupTimeout: NodeJS.Timeout | null = null;
	let runValidateRemoteTimeout: NodeJS.Timeout | null = null;

	if (BROWSER && config.disableMultiple && disabled === false) {
		let submitted = {};
		try {
			submitted = JSON.parse(decode(localStorage.getItem("fc_multi")));
		} catch (e) {}
		if (typeof submitted[key] !== "undefined" && submitted[key] === true) {
			disabled = true;
			disabledType = "multiple";
			disabledMessage = config.disabledMultipleMessage ?? "";
		}
	}

	// Context
	setContext("uploadsInProgress", uploadsInProgress);
	setContext("saveProgressMessage", saveProgressMessage);
	setContext("device", device);
	setContext("iframe", iframe);
	setContext("seamless", seamless);
	setContext("navigating", writable(false));
	setContext("fireGoogleEvent", fireGoogleEvent);
	setContext("config", config);
	setContext("styling", styling);
	setContext("timing", timing);
	setContext("t", t);
	setContext("test", test);
	setContext("plan", plan);
	setContext("stripe", stripe);
	setContext("formStripe", formStripe);
	setContext("visit", visit);
	setContext("visitMeta", visitMeta);
	setContext("fieldDisplay", fieldDisplay);
	setContext("formValues", formValues);
	setContext("extraValues", extraValues);
	setContext("readOnlyValues", readOnlyValues);
	setContext("formKey", key);
	setContext("formMeta", formMeta);
	setContext("sessionId", session_id);
	setContext("countryCode", countryCode);
	setContext("mounted", mounted);
	setContext("lookupLoading", writable([]));
	setContext("formLanguage", formLanguage);
	setContext("currentField", writable({ tracking: null, action: null }));
	setContext("invalid_fields", invalid_fields);
	setContext("remoteValidation", remoteValidation);
	setContext("focusOnErrors", focusOnErrors);
	setContext("direction", writable(formEmbed.direction));

	const lookupLoading = getContext<Writable<string[]>>("lookupLoading");
	const navigating = getContext<Writable<boolean>>("navigating");
	const currentField = getContext<Writable<CurrentField>>("currentField");
	const debug = getContext<Writable<FormResponseDebug[]>>("debug");

	// Every 3 seconds check if $visit has changed
	let visitInterval: any;
	let resizeObserver: ResizeObserver | null = null;
	if (BROWSER) {
		visitInterval = setInterval(checkAndUpdateVisit, 3000);
	}

	function gtag(...args: any) {
		(window as any).dataLayer.push(arguments);
	}

	async function initGoogleAnalytics() {
		if (
			formEmbed.config.enableGoogleAnalytics &&
			formEmbed.config.googleAnalyticsCode &&
			iframe === false
		) {
			gtag("js", new Date());
			gtag("config", formEmbed.config.googleAnalyticsCode, {
				config: true
			});
		}
	}

	async function prepareGoogleEvent(type, data, past_google_events) {
		const hidden_field_kv = Object.fromEntries(
			fieldDisplay
				.filter((field) => {
					return field.visibleComputed && field.type === "Hidden";
				})
				.map((field: HiddenFieldDisplay) => [
					`form_field_${field.id}`,
					field.valueComputed
				])
		);
		if (type === "form_begin" && past_google_events.includes(type) === false) {
			return {
				key: "form_begin",
				type: type,
				attributes: {
					form_name: formEmbed.name,
					...hidden_field_kv
				}
			};
		}
		if (type === "form_submit" && past_google_events.includes(type) === false) {
			return {
				key: "form_submit",
				type: type,
				attributes: {
					form_name: formEmbed.name,
					...hidden_field_kv
				}
			};
		}
		if (type === "form_page_view" && pages.length > 1) {
			const pageLabel = pages.find((page) => page.id === currentPageId)?.label;
			return {
				key: `form_page_view_${currentPageId}`,
				type: type,
				attributes: {
					form_name: formEmbed.name,
					form_page_name: pageLabel,
					...hidden_field_kv
				}
			};
		}
		if (type === "form_purchase") {
			return {
				key: "form_purchase",
				type: "purchase",
				attributes: data
			};
		}
	}

	async function fireGoogleEvent(type: string, data: any = null) {
		if (!BROWSER || !record) return;
		if (disabled) return;
		if (bot) return;
		const event = await prepareGoogleEvent(type, data, past_google_events);
		if (!event) return;
		if (past_google_events.includes(event.key)) return;
		past_google_events.push(event.key);
		if (iframe) {
			if (formEmbed.config.enableGoogleAnalytics) {
				message("ga_event", event);
			}
			if (formEmbed.config.enableGoogleTagManager) {
				message("gtm_event", event);
			}
		}
		if (iframe === false) {
			if (
				formEmbed.config.enableGoogleAnalytics &&
				formEmbed.config.googleAnalyticsCode
			) {
				gtag("event", event.type, event.attributes);
			}
			if (
				formEmbed.config.enableGoogleTagManager &&
				formEmbed.config.googleTagManagerCode
			) {
				if (event.type !== "purchase") {
					(window as any).dataLayer.push({
						event: event.type,
						...event.attributes
					});
				}
			}
		}
	}

	async function checkAndUpdateVisit() {
		const is_partial =
			new URLSearchParams(requestUrl).get("partial") === "true";
		const auto_submit =
			new URLSearchParams(requestUrl).get("auto_submit") === "true";
		if (!BROWSER || !record) return;
		if (disabled && test === false) return;
		if (bot) return;
		if (hasVisitChanged === true) {
			hasVisitChanged = false;
			const preparedVisit = prepareVisit($visit, plan);
			if (JSON.stringify(preparedVisit) === lastVisit) {
				return;
			}
			if (preparedVisit.start) {
				fireGoogleEvent("form_begin");
			}
			lastVisit = JSON.stringify(preparedVisit);
			let response;
			try {
				response = await fetch("/public/form/visit", {
					method: "POST",
					body: JSON.stringify({ ...preparedVisit, nonce }),
					headers: {
						"content-type": "application/json"
					}
				});
			} catch (e) {
				return;
			}
			const data = await response.json();

			if (data.data.nonce) {
				let isFirstTime = false;
				if (nonce === null) {
					isFirstTime = true;
				}
				nonce = data.data.nonce;
				if (isFirstTime) {
					if (is_partial) {
						submitPartialForm("url");
					} else if (auto_submit) {
						submitForm();
					} else {
						submitPartialForm();
					}
				} else if (isLastVisit === false) {
					submitPartialForm();
				}
			}
			if (isLastVisit) {
				clearInterval(visitInterval);
			}
		}
	}

	$: {
		$visit;
		hasVisitChanged = true;
	}

	function defaultFormStripe(): FormStripe {
		const stripeField = fieldDisplay.find(
			(field) => field.type === "CreditCard"
		) as FieldDisplay & { type: "CreditCard" };
		if (!stripeField) {
			return {
				field: "",
				idempotencyKey: uuid(),
				type: "charge",
				currency: "USD",
				manualCapture: false,
				trialPeriodDays: "0",
				customer: {
					fields: [],
					metadata: []
				},
				transaction: {
					charge: [],
					subscription: [],
					metadata: []
				}
			};
		}
		if (!stripeField.customer) {
			stripeField.customer = {
				fields: [],
				metadata: []
			};
		}
		if (!stripeField.transaction) {
			stripeField.transaction = {
				charge: [],
				subscription: [],
				metadata: []
			};
		}
		return {
			field: stripeField.id,
			idempotencyKey: uuid(),
			type: stripeField ? stripeField.settings.type : "charge",
			currency: stripeField ? stripeField.settings.currency : "USD",
			trialPeriodDays: stripeField.settings.trialPeriodDays || "0",
			manualCapture: stripeField.settings.authorizeOnly || false,
			customer: clone(stripeField.customer),
			transaction: clone(stripeField.transaction)
		};
	}

	let canWeEager: (fields: FieldDisplay[]) => void;

	const applyTaintedToFields = (fieldDisplay: FieldDisplay[]) => {
		const newFieldDisplay = clone(fieldDisplay);
		for (let i = 0; i < taintedFields.length; i++) {
			const field_id = taintedFields[i];
			const field = findField(field_id, newFieldDisplay);

			if (field && field.type !== "RichText" && field.type !== "Hidden") {
				field.tainted = true;
			}
		}
		return newFieldDisplay;
	};

	const taintAllFields = (
		fieldDisplay: FieldDisplay[],
		pageId: string = null
	) => {
		taintedFields =
			pageId === null
				? fieldDisplay.map((field) => field.id)
				: fieldDisplay
						.filter((field) => field.page === pageId)
						.map((field) => field.id);
		return applyTaintedToFields(fieldDisplay);
	};

	$: changeCurrentField($currentField.tracking);

	function changeCurrentField(tracking: FieldDisplay | null) {
		if (!tracking) return;
		const field = findField(tracking.id, fieldDisplay);
		if (!field) return;
		if (["MultipleChoice", "Rating", "NPS", "Dropdown"].includes(field.type)) {
			eagerOnNext = true;
		}
	}

	const updateOther = (value: any) => {
		const field_id = value.detail.field_id;
		const field = findField(field_id, fieldDisplay);
		if (!field) return;
		if (value.detail.tainted) {
			if (field.type === "Hidden") return;
			if (field.type === "RichText") return;
			if (!taintedFields.includes(field_id)) taintedFields.push(field_id);
			fieldDisplay = applyTaintedToFields(fieldDisplay);
		}
	};

	const applyLookup = async () => {
		for (const lookup of configDisplay.lookups) {
			let conditionsFulfilled = await areConditionsFulfilled(
				fieldDisplay,
				pageDisplay,
				$formValues,
				lookup.conditions,
				formLanguage.locale
			);

			if (conditionsFulfilled) {
				const { hasChanged, newState } = await hasLookupChanged(
					lookupState,
					lookup,
					fieldDisplay,
					$formValues,
					$extraValues,
					metadata,
					formLanguage.locale
				);

				if (hasChanged === false) {
					continue;
				}

				lookupState[lookup.id].lastLookup = newState;

				// Check if fetch is in progress
				if (lookupState[lookup.id]?.abortController) {
					lookupState[lookup.id].abortController.abort("Not needed");
				}

				if (lookupsRecent > 10) {
					return;
				}

				if (clearLookupsRecent) {
					clearTimeout(clearLookupsRecent);
				}

				lookupsRecent++;

				setTimeout(() => {
					lookupsRecent--;
				}, 10000);

				const { changed, data, state } = performLookup(
					lookupState,
					lookup,
					fieldDisplay,
					$formValues,
					$extraValues,
					metadata,
					session,
					test,
					formEmbed.id,
					formLanguage.locale
				);

				lookupState = state;
				$lookupLoading = [...$lookupLoading, lookup.id];

				if (changed && data) {
					data.then(async (response) => {
						if (response?.error) {
							$debug = [
								...$debug,
								{
									type: "error",
									message: response.error,
									description: response.description ?? null
								}
							];
						}

						$lookupLoading = $lookupLoading.filter(
							(loading) => loading !== lookup.id
						);

						let extraValuesDraft = createDraft($extraValues);

						try {
							// Remove existing lookups
							for (const item of Object.keys(extraValuesDraft)) {
								if (item.startsWith(`${lookup.id}_`)) {
									delete extraValuesDraft[item];
								}
							}

							// Add new ones
							for (const item of Object.keys(response?.data ?? {})) {
								const content = response.data[item];
								const item_key = sanitizeReference(item);
								if (typeof content === "string") {
									extraValuesDraft[`${lookup.id}_${item_key}`] =
										response.data[item];
								} else if (typeof content === "number") {
									extraValuesDraft[`${lookup.id}_${item_key}`] =
										response.data[item].toString();
								} else if (typeof content === "boolean") {
									extraValuesDraft[`${lookup.id}_${item_key}`] = response.data[
										item
									]
										? "true"
										: "false";
								} else {
									extraValuesDraft[`${lookup.id}_${item_key}`] = "";
								}
							}
							$extraValues = finishDraft(extraValuesDraft);
							const newValues = await updateFormValuesBasedOnLookup(
								$formValues,
								$extraValues,
								fields,
								lookup.id,
								formLanguage.locale
							);
							$visit.lookups_performed += 1;
							if (!equal(newValues, $formValues)) {
								$formValues = newValues;
							}
							await applyFormValuesDebounced(newValues);
						} catch (e) {}
					});
				} else {
					if (data && typeof data?.then === "function") {
						data.then(async (response) => {
							if (response?.error) {
								$debug = [
									...$debug,
									{
										type: "error",
										message: response.error,
										description: response.description ?? null
									}
								];
							}
						});
					}
					$lookupLoading = $lookupLoading.filter(
						(loading) => loading !== lookup.id
					);
				}
			}
		}
	};

	const applyValidateRemote = async () => {
		const fieldDisplayCopy = clone(fieldDisplay);
		const emailFields = fieldDisplayCopy.filter(
			(field) => field.type === "Email" && field.settings.unique
		) as EmailFieldDisplay[];
		if (emailFields.length === 0) return;
		for (const emailField of emailFields) {
			const emailValue = $formValues[emailField.id];
			let remoteValidItem = $remoteValidation.find(
				(item) => item.field_id === emailField.id
			);
			if (!emailValue || !isValidEmail(emailValue)) {
				$remoteValidation = $remoteValidation.map((item) => {
					if (item.field_id === emailField.id) {
						item.state = null;
						item.errorType = null;
					}
					return item;
				});
				return;
			}
			$remoteValidation = $remoteValidation.map((item) => {
				if (item.field_id === emailField.id) {
					item.state = "loading";
					item.errorType = null;
				}
				return item;
			});
			const response = await fetch("/public/form/validate-email", {
				method: "POST",
				headers: {
					"content-type": "application/json"
				},
				body: JSON.stringify({
					email: emailValue,
					form_id: formEmbed.id,
					field_id: emailField.id,
					test: test
				})
			});
			const isUniqueResponse = await response.json();
			$remoteValidation = $remoteValidation.map((item) => {
				if (item.field_id === emailField.id) {
					if (isUniqueResponse.data === true) {
						item.state = "valid";
						item.errorType = null;
					} else {
						item.state = "invalid";
						item.errorType = "already_used";
					}
				}
				return item;
			});
		}
	};

	// Look at the email field and see if 'unique' is set. If yes, validate the email field using fetch
	const validateRemote = async () => {
		if (!BROWSER) return;
		if (runValidateRemoteTimeout) {
			clearTimeout(runValidateRemoteTimeout);
		}
		runValidateRemoteTimeout = setTimeout(async () => {
			try {
				await applyValidateRemote();
			} catch (e) {}
		}, 1000);
	};

	const runLookup = async () => {
		if (!BROWSER) return;
		const field = findField($visit.last_field, fields);
		let delay = 700;
		if (
			field === null ||
			(field &&
				[
					"FileUpload",
					"NPS",
					"Rating",
					"Hidden",
					"Calendly",
					"MultipleChoice",
					"Dropdown"
				].includes(field.type))
		) {
			delay = 0;
		}
		if (runLookupTimeout) {
			clearTimeout(runLookupTimeout);
		}
		runLookupTimeout = setTimeout(async () => {
			try {
				await applyLookup();
			} catch (e) {}
		}, delay);
	};

	const saveFormToLocalStorageDebounced = debounce(saveFormToLocalStorage, 500);

	async function focusOnErrors(pageId: string | null = null) {
		fieldDisplay = await taintAllFields(fieldDisplay, pageId);
		await tick();
		const invalidField = document.querySelector(".field.invalid");
		if (invalidField) {
			scrollIntoViewIfNeeded(
				invalidField,
				iframe === true && popup === false ? message : null
			);
		}
	}

	async function applyFormValues(formValues: FormValues) {
		const visibleFieldsBefore = fieldDisplay
			.filter((field) => field.visibleComputed === true)
			.map((field) => field.id);

		({
			fieldDisplay,
			pageDisplay,
			configDisplay,
			formMeta: $formMeta,
			readOnlyValues: $readOnlyValues
		} = await computeFormState(
			fields,
			pages,
			config,
			formValues,
			$extraValues,
			$formStripe,
			metadata,
			formLanguage.locale
		));

		fieldDisplay = applyTaintedToFields(fieldDisplay);

		const newlyAddedFields = fieldDisplay
			.filter(
				(field) =>
					field.visibleComputed === true &&
					!visibleFieldsBefore.includes(field.id)
			)
			.map((field) => field.id);

		newlyVisibleFields = [...newlyVisibleFields, ...newlyAddedFields];
		newlyVisibleFields = [...new Set(newlyVisibleFields)];

		runLookup();
		validateRemote();
		saveFormToLocalStorageDebounced();

		if (
			eagerOnNext &&
			typeof canWeEager === "function" &&
			styling.eagerNavigation !== false
		) {
			eagerOnNext = false;
			canWeEager(fieldDisplay);
		}
	}

	$: {
		currentPageId;
		saveFormToLocalStorageDebounced();
	}

	$: updateInvalidFields(fieldDisplay, currentPageId);

	$: {
		coupon;
		$formValues;
		if ($mounted) applyFormValuesDebounced($formValues);
	}

	$: coupon = $formStripe.coupon;
	$: hiddenPages = pageDisplay
		.filter((page) => page.visibleComputed === false)
		.map((page) => page.id);
	$: useStripe = fieldDisplay.some(
		(field) =>
			field.type === "CreditCard" &&
			field.visibleComputed &&
			!hiddenPages.includes(field.page)
	);

	const createPaymentMethod = async (stripe: StripeInstance) => {
		const customerInfo = await computeFieldMap(
			$formStripe.customer.fields,
			fieldDisplay,
			$formValues,
			metadata
		);
		let billing: CreatePaymentMethodData["billing_details"] = {
			address: {}
		};
		for (let item of customerInfo) {
			if (item.fieldComputed.trim() === "") continue;
			if (item.column === "name") billing.name = item.fieldComputed;
			if (item.column === "email") billing.email = item.fieldComputed;
			if (item.column === "phone") billing.phone = item.fieldComputed;
			if (item.column === "address.city")
				billing.address!.city = item.fieldComputed;
			if (item.column === "address.country")
				billing.address!.country = item.fieldComputed;
			if (item.column === "address.line1")
				billing.address!.line1 = item.fieldComputed;
			if (item.column === "address.line2")
				billing.address!.line2 = item.fieldComputed;
			if (item.column === "address.postal_code")
				billing.address!.postal_code = item.fieldComputed;
			if (item.column === "address.state")
				billing.address!.state = item.fieldComputed;
		}
		const stripePaymentMethod = await stripe.instance.createPaymentMethod({
			type: "card",
			card: stripe.elements.getElement("card")!,
			billing_details: billing
		});
		if (stripePaymentMethod.error) {
			return {
				error: stripePaymentMethod.error,
				data: null
			};
		} else {
			return {
				error: null,
				data: stripePaymentMethod.paymentMethod
			};
		}
	};

	function saveFormToLocalStorage() {
		if (!BROWSER) return;
		if (config.saveProgress === false) return;
		if (saveProgressTimeout) {
			clearTimeout(saveProgressTimeout);
		}
		let oldData = {};
		const old = localStorage.getItem(`fc_${key}`);
		if (old) {
			try {
				oldData = JSON.parse(decode(old));
			} catch (e) {}
		}
		const data = {
			...oldData,
			page: currentPageId,
			formValues: $formValues
		};
		$saveProgressMessage = t.get("saving");
		localStorage.setItem(`fc_${key}`, encode(JSON.stringify(data)));
		saveProgressTimeout = setTimeout(() => {
			$saveProgressMessage = t.get("saved");
		}, 500);
	}

	function getFormFromLocalStorage() {
		if (!BROWSER) return;
		const data = localStorage.getItem(`fc_${key}`);
		if (!data) return;

		try {
			const parsed = JSON.parse(decode(data));
			if (!parsed) return;
			return parsed;
		} catch (e) {
			return null;
		}
	}

	const addToLocalStorage = async (key: string) => {
		let formsSubmitted;
		try {
			formsSubmitted = JSON.parse(decode(localStorage.getItem("fc_multi")));
		} catch (e) {
			formsSubmitted = {};
		}
		formsSubmitted[key] = true;
		localStorage.setItem("fc_multi", encode(JSON.stringify(formsSubmitted)));
	};

	function updateInvalidFields(
		fieldDisplay: FieldDisplay[],
		currentPageId: string
	) {
		$invalid_fields = [];
		if (currentPageId === "welcome") {
			return;
		}
		$invalid_fields = fieldDisplay.filter(
			(field) =>
				("valid" in field &&
					field.valid === false &&
					field.page === currentPageId) ||
				("remoteState" in field &&
					field.remoteState === "error" &&
					field.page === currentPageId)
		);
	}

	const submitPartialForm = async (method: "url" | "config" = "config") => {
		if (navigator.doNotTrack === "1") return;
		if (!BROWSER || !record) return;
		if (method === "url") {
		} else {
			if (!$visitMeta.start) return;
			if (!config.recordPartial) return;
		}
		if (formLoading) return;
		if (isLastVisit) return;
		await applyFormValues($formValues);
		const formValuesFiltered: FormValues = {};
		for (const key in $formValues) {
			const fieldsToRecord = config.recordPartialFields ?? [];
			if (fieldsToRecord.includes(key) || fieldsToRecord.length === 0) {
				formValuesFiltered[key] = $formValues[key];
			}
		}
		if (equal(formValuesFiltered, partialLastRecord)) {
			return;
		}
		const non_empty_form_values = Object.values(formValuesFiltered).filter(
			(x) => x !== ""
		).length;
		if (non_empty_form_values === 0) return;
		const phone_number =
			(document.getElementsByName("phone_number")[0] as HTMLInputElement)
				?.value ?? null;
		submit_partial_controller = new AbortController();
		submit_partial_signal = submit_partial_controller.signal;
		partial_loading = true;
		const submitFormEvent = await fetch(
			`/public/form/submit?test=${test}&key=${key}`,
			{
				method: "POST",
				signal: submit_partial_signal,
				body: JSON.stringify({
					key,
					partial: true,
					formLanguage: formLanguage,
					started: $visitMeta.start,
					existing_response_id: response_id,
					accessCode,
					formValues: formValuesFiltered,
					formStripe: $formStripe,
					extraValues: $extraValues,
					metadata: metadata,
					test,
					session,
					phone_number,
					nonce,
					bot
				}),
				headers: {
					"content-type": "application/json",
					"fly-prefer-region": PUBLIC_FLY_PRIMARY_REGION
				}
			}
		);
		partial_loading = false;
		partialLastRecord = formValuesFiltered;
		const response: any = await submitFormEvent.json();
		if (response.data) {
			localStorage.setItem(`fc_${key}_partial`, encode(response.data));
			response_id = response.data;
		}
	};

	const submitForm = async (e: Event = undefined) => {
		if (formLoading) return;
		if ($navigating) return;
		if (!browser) return;
		$debug = [];
		formLoading = true;
		formError = null;
		fieldDisplay = taintAllFields(fieldDisplay);
		await applyFormValues($formValues);
		if ($invalid_fields.length > 0) {
			formLoading = false;
			focusOnErrors(null);
			return false;
		}
		if ($stripe !== null && useStripe && !$formStripe?.paymentMethod) {
			const stripePaymentMethod = await createPaymentMethod($stripe);
			if (!stripePaymentMethod.data) {
				formLoading = false;
				formError = stripePaymentMethod.error?.message || "Error";
				return;
			} else {
				$formStripe.paymentMethod = stripePaymentMethod.data.id;
			}
		}
		if ($stripe !== null && useStripe) {
			$formStripe.idempotencyKey = uuid();
		}
		const phone_number =
			(document.getElementsByName("phone_number")[0] as HTMLInputElement)
				?.value ?? null;
		$visit.time_spent = Math.round(
			(Date.now() - ($visitMeta.start || Date.now())) / 1000
		);
		metadata.timeSpent = $visit.time_spent;
		let submitFormEvent;
		if (submit_partial_controller) {
			submit_partial_controller.abort("form:submit");
		}
		if (partial_loading) {
			await new Promise((resolve) => setTimeout(resolve, 2000));
		}
		try {
			submitFormEvent = await fetch(
				`/public/form/submit?test=${test}&key=${key}`,
				{
					method: "POST",
					body: JSON.stringify({
						key,
						partial: false,
						formLanguage,
						started: $visitMeta.start,
						existing_response_id: response_id,
						accessCode,
						formValues: $formValues,
						formStripe: $formStripe,
						extraValues: $extraValues,
						metadata: metadata,
						test,
						hubspotutk,
						session,
						phone_number,
						nonce,
						bot
					}),
					headers: {
						"content-type": "application/json",
						"fly-prefer-region": PUBLIC_FLY_PRIMARY_REGION
					}
				}
			);
		} catch (e) {
			console.error();
			formLoading = false;
			formError = `Error - ${e?.message || "Unknown"}. Please try again.`;
			return;
		}
		if (submitFormEvent.status === 500) {
			let result_error;
			let raw_text = await submitFormEvent.text();
			try {
				result_error = JSON.parse(raw_text);
			} catch (e) {
				result_error = raw_text;
			}
			formLoading = false;
			formError = `(500) - ${result_error?.message ?? "Unknown"}. Please try again in some time.`;
			return;
		}
		if (submitFormEvent.status === 400) {
			let response_json, message_show;
			try {
				response_json = await submitFormEvent.json();
				message_show = response_json.message
					? response_json.message.substring(0, 100)
					: "Unknown error";
			} catch (e) {}
			formLoading = false;
			formError = `(400) - ${message_show}. Please try again.`;
			return;
		}
		// For other status codes above 400
		if (submitFormEvent.status >= 400) {
			const common_status_texts = {
				401: "Unauthorized",
				502: "Bad Gateway",
				503: "Service Unavailable",
				504: "Gateway Timeout",
				524: "A timeout occurred"
			};
			formLoading = false;
			formError = `(${submitFormEvent.status}) - ${common_status_texts[submitFormEvent.status] ?? "Unknown"}. Please try again.`;
			return;
		}
		const response: any = await submitFormEvent.json();
		if ($stripe !== null && useStripe) {
			if (response.data?.status === "requires_action") {
				// Customer was created
				if (response.data.customer) {
					$formStripe.customer.id = response.data.customer;
				}
				// Requires further action before confirming
				const { error, paymentIntent } =
					await $stripe.instance.handleNextAction({
						clientSecret: response.data.clientSecret
					});
				formLoading = false;
				if (error || !paymentIntent) {
					formError = error?.message || "Error";
					return;
				} else if (paymentIntent.id) {
					$formStripe.paymentIntent = paymentIntent.id;
					submitForm(e);
					return;
				}
				return;
			} else if (response.data?.status === "reset_method") {
				formLoading = false;
				formError = response.data?.message;
				$formStripe.paymentMethod = undefined;
				return;
			}
		}

		formLoading = false;

		if (response.data?.debug) {
			$debug = response.data.debug;
		}
		if (response.debug) {
			$debug = response.debug;
		}

		if (response.error) {
			formError = response.error;
		} else if (response.data) {
			fireGoogleEvent("form_submit");
			if (response.data.payment) {
				fireGoogleEvent("form_purchase", response.data.payment);
			}
			$formStripe = defaultFormStripe();
			$visit.last_field = null;
			$visit.last_page = null;
			isLastVisit = true;
			$formMeta.formDisabled = true;
			localStorage.removeItem(`fc_${key}_partial`);
			let skipSuccess = false;
			if (config.disableMultiple) {
				addToLocalStorage(key);
			}
			if (response.data.redirect) {
				const redirect = response.data.redirect as RedirectAction;
				const delay = redirect.delay || "0";
				skipSuccess = delay === "0";
				setTimeout(
					() => {
						if (iframe && redirectWithin === false) {
							message("redirect", redirect.url);
						} else {
							const params = redirect.url.split("?");
							const paramsObject = new URLSearchParams(params[1] ?? "");
							if (iframe) {
								paramsObject.set("iframe", "true");
							}
							if (popup) {
								paramsObject.set("popup", "true");
							}
							if (paramsObject.size) {
								window.location.href = `${params[0]}?${paramsObject.toString()}`;
							} else {
								window.location.href = redirect.url;
							}
						}
					},
					parseFloat(delay) * 1000
				);
			}
			if (response.data.success) {
				message(
					"submit:success",
					getFormValuesArray($formValues, fieldDisplay)
				);
				if (skipSuccess) {
					formLoading = true;
				} else {
					const initHeight = formRef.offsetHeight;
					localStorage.removeItem(`fc_${key}`);
					formMessage = makeReadable(response.data.success.content);
					await tick();
					let newHeight = formRef.offsetHeight;
					const maxHeight = windowInnerHeight * 0.8;
					const initHeightNew = Math.min(initHeight, maxHeight);
					newHeight = Math.max(formMessageRef.offsetHeight, initHeightNew);
					const delay = Math.min(Math.max(formMessage.length * 0.035, 2), 10);
					setTimeout(() => {
						message("close", null);
					}, delay * 1000);
					formRef.animate(
						[
							{
								height: `${initHeight}px`
							},
							{
								height: `${newHeight}px`
							}
						],
						{
							duration: $timing.duration,
							easing: "ease-in-out",
							fill: "forwards"
						}
					);
					setTimeout(() => {
						message("success", null);
						if (iframe === false) {
							const rect = formMessageRef.getBoundingClientRect();
							const isVisible = rect.top >= 0 && rect.top <= window.innerHeight;
							if (!isVisible) {
								window.scrollBy({
									top:
										rect.top -
										parseFloat(
											getComputedStyle(document.documentElement).fontSize
										) *
											1,
									behavior: "smooth"
								});
							}
						}
					}, $timing.duration);
				}
			}
		}
	};

	const message = (type: string, content: any) => {
		if (!window || !window.parent) return;
		if (iframe === false) return;
		window.parent.postMessage(
			{
				type,
				content
			},
			"*"
		);
	};

	function prepareUrlReferences(url: string, extraValues: ExtraValues) {
		const extraValuesDraft = createDraft(extraValues);
		for (const item of Object.keys(extraValuesDraft)) {
			if (item.startsWith("get_")) {
				delete extraValuesDraft[item];
			}
		}
		const urlObject = new URL(url);
		const params = urlObject.searchParams;
		for (const key of params.keys()) {
			if (key.toLowerCase().startsWith("field")) continue;
			const sanitizedKey = `get_${sanitizeReference(key)}`;
			extraValuesDraft[sanitizedKey] = params.getAll(key).join(", ");
		}
		extraValues = finishDraft(extraValuesDraft);
		return extraValues;
	}

	const messageFromParent = (event: MessageEvent) => {
		if (event.data.type === "load:ok") {
			bodyStyle = "overflow-y: hidden;";
		}
		if (event.data.type === "url") {
			const url = new URL(event.data.content);
			$visit.tracking = getUTMParameters(event.data.content);
			$visit.tracking.page_url = url.origin + url.pathname;
			$visit.tracking.page_url_raw = url.href;
			const urlValues = getValuesFromUrl(event.data.content, fields);
			const newValues = applyFormValue($formValues, urlValues, fields);
			if (newValues) {
				$formValues = newValues;
			}
			metadata.pageUrl = event.data.content;
			$extraValues = prepareUrlReferences(event.data.content, $extraValues);
		}
		if (event.data.type === "title") {
			metadata.pageTitle = event.data.content;
		}
		if (event.data.type === "windowInnerHeight") {
			windowInnerHeight = event.data.content;
		}
		if (event.data.type === "redirectWithin" && event.data.content === true) {
			redirectWithin = true;
		}
		if (event.data.type === "hubspotutk") {
			hubspotutk = event.data.content;
		}
		if (event.data.type === "values") {
			const values = event.data.content;
			const newValues = applyFormValue($formValues, values, fields);
			if (newValues) {
				$formValues = newValues;
			}
			for (const key of Object.keys(values)) {
				if (
					[
						"page_url",
						"page_url_raw",
						"utm_campaign",
						"utm_content",
						"utm_medium",
						"utm_source",
						"utm_term"
					].includes(key)
				) {
					$visit.tracking[key] = values[key];
				}
			}
		}
	};

	const initIframe = async () => {
		if (iframe === false || BROWSER === false) return;
		message("load", true);
		message("width", styling.formWidth);
		message("name", formEmbed.name);
		message("background", "transparent");
		window.addEventListener("message", messageFromParent);
	};

	const enableResizeObserver = () => {
		if (!BROWSER) return;
		let updates = 0;
		let lastUpdate = Date.now();
		let animationFrameId: number | null = null;

		// Resize observer
		resizeObserver = new ResizeObserver((entries) => {
			const now = Date.now();

			if (now - lastUpdate > 1000) {
				updates = 0;
				lastUpdate = now;
			}

			if (updates < 50) {
				updates++;

				// Cancel previous animation frame if pending
				if (animationFrameId) {
					cancelAnimationFrame(animationFrameId);
				}

				// Defer measurements to avoid ResizeObserver loop
				animationFrameId = requestAnimationFrame(() => {
					if (formCoverRef) {
						try {
							const formStyles = getComputedStyle(formCoverRef);
							const height = parseFloat(formStyles.height.replace("px", ""));
							message("height", height);
						} catch (_) {}
					}
					animationFrameId = null;
				});
			}
		});

		// Observe body
		if (formCoverRef) {
			resizeObserver.observe(formCoverRef);
		}
	};

	$: if (newlyVisibleFields) scrollToNewDebounced();

	function scrollToNew() {
		const fieldNodes = newlyVisibleFields
			.map((field) => document.querySelector(`[data-field-id="${field}"]`))
			.filter(Boolean);
		const scrollTo = findFieldToScroll(fieldNodes);
		if (scrollTo && styling.jumpToNewFields) {
			scrollTo.scrollIntoView({
				behavior: "smooth",
				block: "nearest",
				inline: "nearest"
			});
			newlyVisibleFields = [];
		}
	}

	const scrollToNewDebounced = debounce(scrollToNew, $timing.duration);
	const formValuesDelay = fields.length > 30 ? 150 : 0;
	const applyFormValuesDebounced = debounce(applyFormValues, formValuesDelay);

	function onFormLoad(callback: () => void) {
		if (!BROWSER) return;
		const observer = new IntersectionObserver((entries) => {
			entries.forEach((entry) => {
				if (entry.isIntersecting) {
					callback();
					observer.unobserve(formCoverRef);
				}
			});
		});
		observer.observe(formCoverRef);
	}

	const documentKeydown = (e: KeyboardEvent) => {
		if (e.key === "Escape") {
			message("close", null);
		}
	};

	onDestroy(() => {
		if (BROWSER) {
			document.removeEventListener("keydown", documentKeydown);

			// Clean up ResizeObserver
			if (resizeObserver) {
				resizeObserver.disconnect();
				resizeObserver = null;
			}

			// Clear intervals
			if (visitInterval) {
				clearInterval(visitInterval);
			}
		}
	});

	$: if (BROWSER && formCoverRef) {
		onFormLoad(() => {
			$mounted = true;
			if (disabled === false || test === true) {
				setTimeout(() => {
					checkAndUpdateVisit();
				}, 100);
			}
		});
		document.addEventListener("keydown", documentKeydown);
		enableResizeObserver();
	}

	function getInitialRemoteValidation(fields: Field[]) {
		const temp = [];
		for (const field of fields) {
			if (field.type === "Email" && field.settings.unique) {
				temp.push({
					field_id: field.id,
					errorType: null,
					state: null
				});
			}
		}
		return temp;
	}

	onMount(async () => {
		if (BROWSER) {
			const existing_id = localStorage.getItem(`fc_${key}_partial`);
			if (existing_id) {
				response_id = decode(existing_id);
			} else {
				response_id = uuid();
			}
		}
		if (BROWSER) {
			metadata.pageTitle = document.title;
			metadata.pageUrl = window.location.href;
			const parsed = getFormFromLocalStorage();
			if (parsed && config.saveProgress) {
				const old_duration = $timing.duration;
				$timing.duration = 0;
				if (parsed.page) {
					currentPageId = parsed.page;
				}
				requestAnimationFrame(() => {
					$timing.duration = old_duration;
				});
			}
		}
		initGoogleAnalytics();
		initIframe();
		if (!fromCache && !test && cache === true) {
			fetch(`/public/form/cache?id=${formEmbed.id}`, {
				method: "GET",
				headers: {
					"fly-prefer-region": PUBLIC_FLY_PRIMARY_REGION
				}
			});
		}
		if (
			formEmbed.stripeKeys &&
			fields.some((field) => field.type === "CreditCard") &&
			BROWSER
		) {
			const stripeField = fieldDisplay.find(
				(field) => field.type === "CreditCard"
			) as FieldDisplay & { type: "CreditCard" };
			const loadStripe = (await import("@stripe/stripe-js/pure")).loadStripe;
			const instance = await loadStripe(
				stripeField.settings.test
					? formEmbed.stripeKeys.publishableKeyTest
					: formEmbed.stripeKeys.publishableKey
			);
			if (!instance) {
				return;
			}
			$stripe = {
				instance: instance,
				publishableKey: formEmbed.stripeKeys.publishableKey,
				publishableKeyTest: formEmbed.stripeKeys.publishableKeyTest,
				elements: {} as any
			};
		}
	});
	let bodyStyle = "";
	let formStyle = "";
	let formStyleDesktop = "";
	if (iframe === false) {
		formStyleDesktop += `width: ${styling.formWidth}px; max-width: calc(100vw - 2rem);`;
		if (seamless) {
			formStyle += "width: 100%;";
		}
	}
	if (seamless) {
		formStyle += "background: transparent;";
	}
	$: hideForm = test === false && disabled;
	$: baseStyling = styling.baseStyling;
	$: welcomeStyling = styling.welcomeCustom
		? styling.welcomeStyling
		: baseStyling;
</script>

<svelte:head>
	{@html `<${""}style>${formEmbed.css}</${""}style>`}
	{@html `<${""}style>
	#form { ${formStyle} }
	@container (width > 48rem) {
		#form { ${formStyleDesktop} }
	}
	</${""}style>`}
	{#if plan.styling_level > 0}
		{@html `<${""}style>${styling.customCSS ?? ""}</${""}style>`}
	{/if}
	{#if formEmbed.config.enableGoogleAnalytics && formEmbed.config.googleAnalyticsCode && iframe === false}
		<script
			async
			src="https://www.googletagmanager.com/gtag/js?id={formEmbed.config
				.googleAnalyticsCode}"></script>
		<script>
			window.dataLayer = window.dataLayer || [];
		</script>
	{/if}
	{#if formEmbed.config.enableGoogleTagManager && formEmbed.config.googleTagManagerCode && iframe === false}
		{@html `<script>
			(function (w, d, s, l, i) {
				w[l] = w[l] || [];
				w[l].push({ "gtm.start": new Date().getTime(), event: "gtm.js" });
				var f = d.getElementsByTagName(s)[0],
					j = d.createElement(s),
					dl = l != "dataLayer" ? "&l=" + l : "";
				j.async = true;
				j.src = "https://www.googletagmanager.com/gtm.js?id=" + i + dl;
				f.parentNode.insertBefore(j, f);
			})(
				window,
				document,
				"script",
				"dataLayer",
				"<CODE>"
			);
		</script>`.replace("<CODE>", formEmbed.config.googleTagManagerCode)}
	{/if}
</svelte:head>

<svelte:body
	class="form-body"
	class:seamless="{seamless}"
	class:iframe="{iframe}"
	style="{bodyStyle}" />

{#if config.saveProgress && config.saveProgressMessage && (iframe === false || seamless === false)}
	<div class="save-progress-indicator">
		{$saveProgressMessage}
	</div>
{/if}

{#if iframe && popup}
	<button
		type="button"
		class="close-button"
		tabindex="-1"
		on:click="{() => {
			message('close', null);
		}}">
		<svg
			width="16"
			height="16"
			xmlns="http://www.w3.org/2000/svg"
			viewBox="0 0 256 256">
			<line
				x1="200"
				y1="56"
				x2="56"
				y2="200"
				stroke="currentColor"
				stroke-linecap="round"
				stroke-linejoin="round"
				stroke-width="28">
			</line>
			<line
				x1="200"
				y1="200"
				x2="56"
				y2="56"
				stroke="currentColor"
				stroke-linecap="round"
				stroke-linejoin="round"
				stroke-width="28"></line>
		</svg>
	</button>
{/if}

{#if currentPageId !== "welcome" && formMessage === null && styling.showProgress === true && $mounted}
	{#await import("$basic/Progress.svelte") then c}
		<div
			class="progress-container"
			in:pageIn="{{ direction: 'right', duration: $timing.duration }}">
			{#if BROWSER && window.innerWidth > 768}
				<svelte:component
					this="{c.default}"
					progress="{form}"
					size="{21}"
					width="{5}" />
			{:else}
				<svelte:component this="{c.default}" progress="{form}" type="bar" />
			{/if}
		</div>
	{/await}
{/if}

{#if seamless === false}
	<div class="fixed-cover">
		{#if areBackgroundsEqual(baseStyling.background, welcomeStyling.background)}
			<BackgroundLayers data="{baseStyling.background}" />
		{:else}
			<BackgroundLayers
				data="{currentPageId === 'welcome'
					? welcomeStyling.background
					: baseStyling.background}" />
		{/if}
	</div>
{/if}

<div bind:this="{formCoverRef}" id="form-cover" dir="{formEmbed.direction}">
	{#if askAccessCode}
		{#await import("$form/Access.svelte") then c}
			<svelte:component
				this="{c.default}"
				bind:askAccessCode="{askAccessCode}"
				bind:accessCode="{accessCode}"
				form="{key}"
				test="{test}" />
		{/await}
	{:else}
		<form
			on:submit|preventDefault="{submitForm}"
			bind:this="{formRef}"
			class="form"
			id="form">
			{#if test === false && disabled}
				<div class="form-disabled-message">
					{#if disabledMessage}
						{disabledMessage}
					{:else if disabledType === "config"}
						{t.get("form_disabled")}
					{:else if disabledType === "multiple"}
						{t.get("form_disabled_multiple")}
					{:else if disabledType === "country"}
						{t.get("form_disabled_country")}
					{/if}
				</div>
			{:else if formMessage !== null}
				<div
					class="form-message"
					bind:this="{formMessageRef}"
					in:pageIn="{{
						distance: 40,
						direction: 'right',
						start: 1 - $timing.in,
						duration: $timing.duration * 1.2,
						easingDistance: bezier(0, 1.15, 0.5, 0.95),
						easingOpacity: sineIn
					}}">
					<div class="form-message-content">
						{#if styling.successCheckmark}
							<Checkmark />
						{/if}
						<div class="richtext richtext-success" id="form-success-message">
							{@html formMessage}
						</div>
					</div>
				</div>
			{:else}
				<div class="form-content">
					<MultiStep
						formLoading="{formLoading}"
						hidden="{hideForm}"
						seamless="{seamless}"
						fieldDisplay="{fieldDisplay}"
						pageDisplay="{pageDisplay}"
						formError="{formError}"
						bind:currentPageId="{currentPageId}"
						bind:canWeEager="{canWeEager}"
						on:updateOther="{updateOther}" />
				</div>
			{/if}
			{#each pageDisplay as page (page.id)}
				{@const currentFields = fieldDisplay.filter(
					(x) =>
						x.visibleComputed === true &&
						"valueComputed" in x &&
						x.page === page.id &&
						page.visibleComputed === true
				)}
				{#each currentFields as field (field.id)}
					{#if "valueComputed" in field}
						<input
							class="hidden_form_field"
							hidden="{true}"
							aria-hidden="true"
							type="text"
							name="{field.id}"
							tabindex="-1"
							value="{typeof field.valueComputed === 'object'
								? JSON.stringify(field.valueComputed)
								: field.valueComputed}" />
					{/if}
				{/each}
			{/each}
		</form>
	{/if}
</div>
